/**
 * Date Suggestions
 * Pure date parsing logic for the inline date chip picker.
 * No React dependencies - all date-fns. Testable with a fixed `now` parameter.
 */

import {
  format,
  addDays,
  addWeeks,
  startOfWeek,
  nextDay,
  setDate,
  getDate,
  getDaysInMonth,
  addMonths,
  isSameDay,
} from 'date-fns';

// ============================================================================
// Types
// ============================================================================

export type DateSuggestionIcon = 'today' | 'tomorrow' | 'calendar' | 'clock' | 'none';

export interface DateSuggestion {
  /** Display label: "Today", "Thu, Mar 12" */
  label: string;
  /** Secondary detail: "Tue", "Mar 12", "12:30 PM" */
  detail: string;
  /** Date in YYYY-MM-DD format, undefined = no date */
  date?: string;
  /** Time in HH:mm format, undefined = no time */
  time?: string;
  /** Icon hint for rendering */
  icon: DateSuggestionIcon;
}

// ============================================================================
// Constants
// ============================================================================

const DAY_NAMES: Record<string, Day> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

// ============================================================================
// Helpers
// ============================================================================

function formatDateStr(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function relativeDay(d: Date, now: Date): 'today' | 'tomorrow' | null {
  if (isSameDay(d, now)) return 'today';
  if (isSameDay(d, addDays(now, 1))) return 'tomorrow';
  return null;
}

function formatLabel(d: Date, now: Date): string {
  const relative = relativeDay(d, now);
  if (relative === 'today') return 'Today';
  if (relative === 'tomorrow') return 'Tomorrow';
  return format(d, 'EEE, MMM d');
}

function formatDetail(d: Date, now: Date): string {
  if (relativeDay(d, now)) return format(d, 'EEE');
  return format(d, 'MMM d');
}

function resolveIcon(d: Date, now: Date, icon?: DateSuggestionIcon): DateSuggestionIcon {
  return icon ?? relativeDay(d, now) ?? 'calendar';
}

function makeSuggestion(d: Date, now: Date, icon?: DateSuggestionIcon, time?: string): DateSuggestion {
  return {
    label: formatLabel(d, now),
    detail: time ? format(d, 'EEE') + ', ' + formatTime12h(time) : formatDetail(d, now),
    date: formatDateStr(d),
    time,
    icon: resolveIcon(d, now, icon),
  };
}

/** Format "HH:mm" to "h:mm AM/PM" */
export function formatTime12h(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, '0')} ${suffix}`;
}

// ============================================================================
// Default suggestions (shown when input is empty)
// ============================================================================

function getDefaultSuggestions(now: Date): DateSuggestion[] {
  const tomorrow = addDays(now, 1);
  const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
  // If today is Monday, "this week" = today, so show next Monday instead
  const thisWeekDate = thisMonday <= now ? addWeeks(thisMonday, 1) : thisMonday;
  const nextWeekDate = addWeeks(thisWeekDate, 1);

  return [
    makeSuggestion(now, now, 'today'),
    makeSuggestion(tomorrow, now, 'tomorrow'),
    {
      label: 'This week',
      detail: format(thisWeekDate, 'EEE, MMM d'),
      date: formatDateStr(thisWeekDate),
      icon: 'calendar',
    },
    {
      label: 'Next week',
      detail: format(nextWeekDate, 'EEE, MMM d'),
      date: formatDateStr(nextWeekDate),
      icon: 'calendar',
    },
    {
      label: 'No date',
      detail: 'No due date',
      date: undefined,
      icon: 'none',
    },
  ];
}

// ============================================================================
// Input Parsing
// ============================================================================

function parseTimeInput(input: string): { hours: number; minutes: number } | null {
  // Match H:MM or HH:MM
  const match = input.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

function parseDayOfMonth(input: string, now: Date): Date | null {
  const num = parseInt(input, 10);
  if (isNaN(num) || num < 1 || num > 31) return null;

  const todayDate = getDate(now);
  const daysInCurrentMonth = getDaysInMonth(now);

  // Try current month first
  if (num >= todayDate && num <= daysInCurrentMonth) {
    return setDate(now, num);
  }

  // Fall back to next month
  const nextMonth = addMonths(now, 1);
  const daysInNextMonth = getDaysInMonth(nextMonth);
  if (num <= daysInNextMonth) {
    return setDate(nextMonth, num);
  }

  return null;
}

// ============================================================================
// Main Export
// ============================================================================

export function getDateSuggestions(input: string, now: Date): DateSuggestion[] {
  const trimmed = input.trim().toLowerCase();

  if (!trimmed) {
    return getDefaultSuggestions(now);
  }

  const suggestions: DateSuggestion[] = [];

  // "today" / "tod"
  if ('today'.startsWith(trimmed)) {
    suggestions.push(makeSuggestion(now, now, 'today'));
  }

  // "tomorrow" / "tom"
  if ('tomorrow'.startsWith(trimmed)) {
    suggestions.push(makeSuggestion(addDays(now, 1), now, 'tomorrow'));
  }

  // Weekday names: "mon", "tue", etc.
  for (const [name, dayIndex] of Object.entries(DAY_NAMES)) {
    if (name.startsWith(trimmed) || trimmed.startsWith(name)) {
      const next = nextDay(now, dayIndex as Day);
      suggestions.push(makeSuggestion(next, now, 'calendar'));
    }
  }

  // Time input: "12:30", "9:00"
  const parsedTime = parseTimeInput(trimmed);
  if (parsedTime) {
    const timeStr = `${parsedTime.hours.toString().padStart(2, '0')}:${parsedTime.minutes.toString().padStart(2, '0')}`;
    suggestions.push(makeSuggestion(now, now, 'clock', timeStr));
  }

  // Day of month: numbers 1-31
  if (/^\d{1,2}$/.test(trimmed)) {
    const dateFromNum = parseDayOfMonth(trimmed, now);
    if (dateFromNum) {
      suggestions.push(makeSuggestion(dateFromNum, now, 'calendar'));
    }
  }

  // "this week"
  if ('this week'.startsWith(trimmed) && trimmed.length >= 4) {
    const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
    const thisWeekDate = thisMonday <= now ? addWeeks(thisMonday, 1) : thisMonday;
    suggestions.push({
      label: 'This week',
      detail: format(thisWeekDate, 'EEE, MMM d'),
      date: formatDateStr(thisWeekDate),
      icon: 'calendar',
    });
  }

  // "next week"
  if ('next week'.startsWith(trimmed) && trimmed.length >= 4) {
    const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
    const thisWeekDate = thisMonday <= now ? addWeeks(thisMonday, 1) : thisMonday;
    const nextWeekDate = addWeeks(thisWeekDate, 1);
    suggestions.push({
      label: 'Next week',
      detail: format(nextWeekDate, 'EEE, MMM d'),
      date: formatDateStr(nextWeekDate),
      icon: 'calendar',
    });
  }

  // "no date"
  if ('no date'.startsWith(trimmed) && trimmed.length >= 2) {
    suggestions.push({
      label: 'No date',
      detail: 'No due date',
      date: undefined,
      icon: 'none',
    });
  }

  // Deduplicate by date+time
  const seen = new Set<string>();
  return suggestions.filter((s) => {
    const key = `${s.date ?? 'none'}-${s.time ?? 'none'}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
